<template>
  <widget-content
    :config="config"
    variant="number"
    @refresh="$emit('refresh')"
  >
    <span
      class="h2 mb-0 d-flex align-items-center"
    >
      <span
        ref="value"
        class="tooltip-wrapper"
      >
        {{ display }}
      </span>
      <span class="h6 mb-0 ml-2 text-right">
        <span
          ref="progress"
          class="tooltip-wrapper"
          v-html="progressFormatted"
        ></span>
        <!--
        <small
          v-if="progressLabel"
          class="d-block text-nowrap text-muted mt-1"
          v-html="progressLabel"
        ></small>
        -->
      </span>
    </span>
    <small
      v-if="valueLabel"
      :class="[
        'd-block text-muted text-nowrap mt-1'
      ]"
    >
      <span v-html="valueLabel"></span>
    </small>
    <div
      v-if="goalTarget !== null"
      class="mt-4"
    >
      <bullet-graph
        ref="bullet"
        class="mt-3 pt-2 pb-1"
        :inverse="inverse"
        :progress="progress"
        :comparative-progress="comparativeProgress"
        :comparative-progress-target="comparativeProgressTarget"
        :comparative-progress-label="comparativeProgressLabel"
        :variant="bulletGraphVariant"
      >
        <template
          v-if="goalTarget !== null"
          v-slot:tooltip
        >
          <span v-html="comparativeProgressTooltip"></span>
        </template>
      </bullet-graph>
      <div class="mt-4 d-flex align-items-start pb-1">
        <span class="h6 mb-0">
          <div
            class="d-block text-nowrap text-muted mb-1"
          >
            <i class="fa fa-fw fa-bullseye"></i>
            <span
              v-if="targetLabel"
            >GOAL</span>
          </div>
          <small
            class="d-block text-nowrap text-muted mb-1"
          >
            <span
              v-if="targetLabel"
              v-html="targetLabel"
            ></span>
          </small>
          <span
            ref="target"
            class="tooltip-wrapper h5"
          >
            {{ goalTargetFormatted }}
          </span>
        </span>
      </div>
    </div>
    <b-tooltip
      :target="() => $refs['value']"
      placement="topright"
      variant="flat-peter-river"
    >
      Current <b>{{ displayTitle }}</b>
    </b-tooltip>
    <b-tooltip
      v-if="goalTarget !== null"
      :target="() => $refs['target']"
      placement="topright"
      variant="flat-peter-river"
    >
      Target <b>{{ displayTitle }}</b>
    </b-tooltip>
    <b-tooltip
      v-if="goalTarget !== null"
      :target="() => $refs['progress']"
      placement="topleft"
      variant="flat-peter-river"
    >
      Progress Compared to Last Year
    </b-tooltip>
  </widget-content>
</template>

<style lang="scss" scoped>
.card-body {
  small {
    font-size: 0.7rem;
  }
}
.card-footer {
  small {
    font-size: 0.7rem;
  }
}
</style>

<script>
import { get } from 'lodash'
import numeral from 'numeral'
import BulletGraph from '@/components/BulletGraph'
import WidgetContent from './components/WidgetContent'
import { strip_tags } from '@/lib/utils'

export default {
  name: 'NumberWidget',

  components: {
    BulletGraph,
    WidgetContent
  },

  props: {
    title: {
      type: String,
      default: ''
    },

    config: {
      type: Object,
      required: true
    }
  },

  computed: {
    value () {
      return get(this.config, 'value', '')
    },

    valueLabel () {
      return get(this.config, 'valueLabel', '&nbsp;')
    },

    comparative () {
      return get(this.config, 'goal.comparative', null)
    },

    targetLabel () {
      return get(this.config, 'goal.targetLabel', null)
    },

    progress () {
      if (this.goalTarget === null) {
        return null
      } else if (this.goalTarget < 0 && this.value < 0) {
        return 0
      } else if (this.goalTarget === 0) {
        return (this.value > 0 ? 100 : 0)
      } else if (get(this.config, 'progress', null) !== null) {
        return get(this.config, 'progress')
      } else {
        return Math.round(this.value / this.goalTarget * 10000) / 100;
      }
    },

    bulletGraphVariant () {
      return get(this.config, 'options.variant')
    },

    progressFormatted () {
      let formatted = get(this.config, 'progressFormatted', null)
      if (formatted === null && this.progress !== null) {
        formatted = this.progress + '%'
      }
      return formatted
    },

    progressLabel () {
      return get(this.config, 'progressLabel', null)
    },

    comparativeProgress () {
      if (this.comparative !== null) {
        let progress = get(this.config, 'goal.comparativeProgress')
        if (progress === null || progress === undefined) {
          let base = Math.max(this.goalTarget, this.value)
          if (base === 0) {
            progress = 0
          } else {
            progress = Math.round(this.comparative / Math.max(this.goalTarget, this.value) * 10000) / 100;
          }
        }
        return progress
      } else {
        return null
      }
    },

    comparativeProgressTarget () {
      return get(this.config, 'goal.comparativeProgressTarget')
    },

    comparativeFormat () {
      return get(this.config, 'comparativeFormat') || this.format
    },

    comparativeFormatted () {
      return this.comparativeFormat.length && this.comparative !== null ? numeral(this.comparative).format(this.comparativeFormat) : null
    },

    comparativeProgressLabel () {
      return get(this.config, 'goal.comparativeProgressLabel') || this.comparativeFormatted
    },

    comparativeProgressTooltip () {
      return get(this.config, 'goal.comparativeProgressTooltip') || `<b>${this.displayTitle}</b>, Last Year`
    },

    inverse ()
    {
      return get(this.config, 'inverse', false)
    },

    format () {
      return get(this.config, 'format', '')
    },

    display () {
      if (String.valueOf(this.value).length < 0) {
        return ''
      }

      if (this.format.length) {
        return numeral(this.value).format(this.format)
      } else {
        return this.value
      }
    },

    goalTarget () {
      return get(this.config, 'goal.target', null)
    },

    goalTargetFormatted () {
      return this.format.length ? numeral(this.goalTarget).format(this.format) : this.goalTarget
    },

    displayTitle () {
      return strip_tags(this.title || get(this.config, 'title'))
    }
  }
}
</script>
